import type { Value } from '../value.ts';
import type { Session } from './Session.ts';

/**
 * A transcript-derived binding: a dataset `name` and the *unevaluated*
 * producing `expression` (the raw producer-tool s-expression string,
 * exactly as the agent authored it and as it is persisted on the
 * tool-call part). The produced value is never stored — it is
 * re-derived on demand by evaluating `expression` against the strict
 * prefix of recipes that precede this one.
 */
export interface Recipe {
  readonly name: string;
  readonly expression: string;
}

/**
 * Pure, transcript-derived replacement for the old mutable binding
 * store. A resolver is an immutable snapshot of the ordered producer
 * recipes visible at one position in the active message branch.
 * Resolution is lazy, recursive, and memoized through `session.fetch`;
 * there is no shared mutable namespace and nothing to hydrate up front.
 */
export interface BindingResolver {
  /** Ordered producer recipes visible at this position (creation order). */
  readonly recipes: readonly Recipe[];
  /**
   * Synchronous membership check — is `name` produced by some recipe in
   * scope? Backs the evaluator's set-ref vs per-row push-down guard,
   * which only needs the boolean, never the resolved value.
   */
  readonly has: (name: string) => boolean;
  /**
   * Resolve `name` to its `Value` by evaluating the *last* recipe that
   * produces it (last-wins, mirroring active-branch replay order)
   * against the strict prefix of recipes before it. Memoized per
   * `(name, prefix-including-producer)` through `session.fetch`, so a
   * diamond dependency collapses to a single evaluation.
   */
  readonly resolve: (name: string) => Promise<Value>;
}

/**
 * Evaluate a producer `expression` with its free dataset names scoped
 * by `bindings`. The React/chat layer supplies this — it closes over
 * the non-serializable engine handles (CDF client, source fetchers,
 * op/verb tables) and threads `bindings` onto the `EvalContext` so a
 * nested `(col <name> …)` resolves recursively against the prefix.
 */
export type ScopedEvaluate = (expression: string, bindings: BindingResolver) => Promise<Value>;

const noProducer = (name: string): Promise<never> =>
  // `has` gates `resolve` in the symbol-resolution path, so reaching
  // here is an engine-invariant violation, not a user-facing
  // unbound-symbol case (env.ts owns the suggestion-rich error).
  Promise.reject(new Error(`bindingResolver: no producer recipe for "${name}"`));

/**
 * Build a {@link BindingResolver} over an ordered recipe snapshot.
 *
 * `resolve(name)` finds the last recipe producing `name`, evaluates its
 * `expression` against a child resolver scoped to the *strict prefix*
 * before that producer (so a recipe can only reference names produced
 * earlier — recursion terminates on a strictly shrinking prefix), and
 * memoizes the result through `session.fetch`. The cache key is the
 * recipe prefix up to and including the producer: two branches that
 * share that prefix correctly share one cache entry (the value is a
 * pure function of the prefix). The `Recipe[]` is a serializable-safe
 * TanStack query-key element — structural hashing is handled by the
 * session's `fetch` cache; no hand-rolled signature string.
 */
export const createBindingResolver = (
  recipes: readonly Recipe[],
  session: Pick<Session, 'fetch'>,
  evaluate: ScopedEvaluate,
): BindingResolver => ({
  recipes,
  has: (name) => recipes.some((r) => r.name === name),
  resolve: (name) => {
    const i = recipes.findLastIndex((r) => r.name === name);
    if (i === -1) {
      return noProducer(name);
    }
    const producer = recipes[i] as Recipe;
    const prefix = recipes.slice(0, i);
    return session.fetch(['binding', name, recipes.slice(0, i + 1)], () =>
      evaluate(producer.expression, createBindingResolver(prefix, session, evaluate)),
    );
  },
});

/**
 * The empty resolver: no recipes in scope. Used by `defaultEvalCtx()`
 * and non-chat callers (CLI, engine unit tests) that never reference a
 * cross-turn dataset. `resolve` is unreachable in practice (gated by
 * `has` in the symbol-resolution path).
 */
export const emptyBindingResolver: BindingResolver = {
  recipes: [],
  has: () => false,
  resolve: noProducer,
};
/**
 * A resolver whose entries are bound imperatively rather than
 * projected from a message transcript — the non-chat counterpart to
 * {@link createBindingResolver}. REPL / CLI sessions and engine unit
 * tests bind a name directly to an already-evaluated `Value` and
 * evaluate against it; `defaultEvalCtx()` uses this so cross-call
 * bindings persist across evaluations that share one context (its
 * documented contract). The chat path never uses this — production
 * cross-turn resolution is the pure transcript projection.
 */
export interface MutableBindingResolver extends BindingResolver {
  /** Imperatively bind `name` to a pre-evaluated `value`. */
  readonly set: (name: string, value: Value) => void;
}

export const createMutableBindingResolver = (): MutableBindingResolver => {
  const values = new Map<string, Value>();
  return {
    // Synthetic recipes — only the names are load-bearing here
    // (`UnboundSymbolError` did-you-mean pooling); the value channel
    // is the map, not recipe replay, so `has`/`resolve` are overridden.
    get recipes(): readonly Recipe[] {
      return [...values.keys()].map((name) => ({ name, expression: `<bound:${name}>` }));
    },
    has: (name) => values.has(name),
    resolve: (name) => (values.has(name) ? Promise.resolve(values.get(name) as Value) : noProducer(name)),
    set: (name, value) => {
      values.set(name, value);
    },
  };
};
